'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { BellIcon } from '@phosphor-icons/react/dist/ssr/Bell'
import { cx } from 'class-variance-authority'
import { ScrollArea } from '@skop-app/lib-front'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'
import type { DynamicIconName } from '@/components/atoms/DynamicIcon'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms/Popover'
import { NumberBadge } from '@/components/atoms'
import { useNotifications } from '@/hooks/useNotifications'
import { useUpdateProjectInvit } from '@/services/mutations/notification'
import { useQueryClient } from '@tanstack/react-query'
import { useUserContext } from '@/context/UserContext'
import { Notification } from '@/types/notification'
import { reviewKeys } from '@/services/queries/review.keys'

type NotificationsProps = {
  component?: string
}

export const Notifications = ({ component = '' }: NotificationsProps) => {
  const router = useRouter()
  const { notifications, streamUnreadError } = useNotifications()

  const [showNotifications, setShowNotifications] = useState(false)

  const nbrOfNotifications = notifications?.length || 0

  const showAnimatedBell =
    component !== 'navbar' && !showNotifications && nbrOfNotifications
  const hasActiveNotifications =
    component !== 'navbar' && nbrOfNotifications > 0

  return (
    <Popover open={showNotifications} onOpenChange={setShowNotifications}>
      <PopoverTrigger>
        <div
          className={cx(
            'relative grid size-10 place-content-center rounded-full transition-colors',
            {
              'grid bg-transparent text-border ring-offset-black hover:bg-slate-800 mdh:hidden':
                component === 'navbar',
              'bg-neutral-100 text-slate-500 ring-offset-white hover:text-slate-300':
                component !== 'navbar' && !hasActiveNotifications,
              'text-slate-800': hasActiveNotifications,
              'outline-none ring-2 ring-primary-500': showNotifications,
            },
          )}
        >
          {hasActiveNotifications && (
            <span className="absolute inset-0.5 rounded-full bg-orange-500 ring-1 ring-inset ring-primary-700/20" />
          )}
          {showAnimatedBell ? (
            <div className="relative z-10 flex size-6 items-center justify-center overflow-hidden">
              <Image
                src="/img/animatedBell.gif"
                alt="bell"
                className="size-9 object-cover"
                width={0}
                height={0}
                unoptimized
              />
            </div>
          ) : (
            <BellIcon className="relative z-10 size-6" aria-hidden="true" />
          )}
          <NumberBadge count={nbrOfNotifications} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="mt-2 bg-white p-2 text-sm text-slate-300">
        {streamUnreadError && (
          <p className="p-2 text-center text-slate-500">
            Les messages sont temporairement indisponibles.
          </p>
        )}
        {!nbrOfNotifications && !streamUnreadError && (
          <p className="p-2 text-center"> Aucune notification</p>
        )}
        <NotificationContainer
          notifications={notifications}
          router={router}
          setShowNotifications={setShowNotifications}
        />
      </PopoverContent>
    </Popover>
  )
}

const NotificationContainer = ({
  notifications,
  router,
  setShowNotifications,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateProjectInvit } = useUpdateProjectInvit()
  const { user } = useUserContext()

  if (!notifications?.length) return null

  const handleNotification = async ({
    id,
    type,
    userID,
    href,
  }: NotificationItemProps) => {
    setShowNotifications(false)

    if (type === 'PROJECT' && userID) {
      try {
        const input = {
          id,
          seen: true,
          userID,
          type,
        }
        await updateProjectInvit(input)
        await queryClient.refetchQueries({
          queryKey: ['notifications', userID],
        })
      } catch (error) {
        console.error('Failed to update project invitation:', error)
      }
    }

    if (type === 'REVIEW_MENTION') {
      try {
        await queryClient.invalidateQueries({
          queryKey: reviewKeys.drawerPrefix,
          refetchType: 'active',
        })
      } catch (error) {
        console.error('Failed to refresh review notifications:', error)
      }

      router.push(href)
      return
    }

    if (user?.id) {
      await queryClient.refetchQueries({
        queryKey: ['request'],
      })
    }
    router.push(href)
  }

  return (
    <ScrollArea className="max-w-md flex-auto bg-white text-sm leading-6">
      <div className="max-h-[300px] lg:max-h-[400px] xl:max-h-[500px]">
        {notifications?.length ? (
          notifications.map(
            ({ id, type, userID, message, icon, href, date }: Notification) => (
              <NotificationItem
                key={id}
                id={id}
                type={type}
                userID={userID}
                message={message}
                icon={icon as DynamicIconName}
                href={href}
                date={date}
                handleNotification={handleNotification}
              />
            ),
          )
        ) : (
          <div className="flex flex-col items-center justify-center p-4">
            <p className="text-center text-slate-500">Aucune notification</p>
          </div>
        )}
      </div>
    </ScrollArea>
  )
}

type NotificationItemProps = {
  id: string
  type: string
  userID?: string
  message: string
  icon: DynamicIconName
  href: string
  date: string
  handleNotification: (props: NotificationItemProps) => Promise<void>
}

const NotificationItem = (props: NotificationItemProps) => {
  const { message, icon, handleNotification } = props

  return (
    <button
      onClick={async () => await handleNotification(props)}
      className="group relative my-1 flex w-full cursor-pointer py-1 hover:rounded-md hover:bg-orange-500"
    >
      <div className="mr-2 flex size-10 flex-none items-center justify-center rounded-lg bg-orange-500">
        <DynamicIcon
          name={icon}
          className="size-6 text-slate-500"
          aria-hidden="true"
        />
      </div>
      <p className="line-clamp-2 px-0.5 text-left text-sm leading-tight text-gray-600 group-hover:text-slate-800">
        {message}
      </p>
    </button>
  )
}
